import { AuthService } from '../auth/auth.service';
import { EppService } from '../epp/epp.service';
import { SmwService } from '../smw/smw.service';
import { ErpService } from './erp.service';

import { ColorMappingPipe } from '../epp/pipes/color-mapping.pipe';
import { GetLegendPipe } from '../pipes/get-legend.pipe';
import { HasLegendPipe } from '../pipes/has-legend.pipe';
import { IsNilPipe } from '../pipes/is-nil.pipe';
import { DatexPipe } from '../shared/datex.pipe';
import { RoundPipe } from '../shared/round.pipe';
import { CheckRegionForParametersetPipe } from './pipes/check-region-for-parameterset.pipe';
import { ParameterClassPipe } from './pipes/parameter-class.pipe';
import { StationNamePipe } from './pipes/station-name.pipe';

import { ResetInputMatAutocompleteDirective } from '../shared/directives/reset-input-mat-autocomplete.directive';
import { WmIconDirective } from '../shared/wm-icon.directive';

import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  MatDivider,
  MatListItemIcon,
  MatListItemLine,
  MatListOption,
  MatSelectionList,
  MatSelectionListChange,
} from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import {
  MatTab,
  MatTabChangeEvent,
  MatTabContent,
  MatTabGroup,
  MatTabLabel,
} from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { stationName } from './constants/constants';
import { TippyDirective } from '@ngneat/helipopper';
import moment from 'moment';
import { map } from 'rxjs';
import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  MatSidenavModule,
  MatIconModule,
  MatButtonModule,
  RouterModule,
  FormsModule,
  ReactiveFormsModule,
  MatTooltipModule,
  MatFormFieldModule,
  MatSelectModule,
  TranslateModule,
  MatExpansionModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [
  StationNamePipe,
  RoundPipe,
  HasLegendPipe,
  GetLegendPipe,
  ColorMappingPipe,
  ParameterClassPipe,
  CheckRegionForParametersetPipe,
  IsNilPipe,
];
export const DIRECTIVES = [
  ResetInputMatAutocompleteDirective,
  TippyDirective,
  WmIconDirective,
  CdkDropList,
  CdkDrag,
  CdkDragHandle,
];

@Component({
  selector: 'app-erp',
  templateUrl: './erp.component.html',
  styleUrls: ['./erp.component.scss'],
  providers: [ErpService],
  animations: [
    trigger('showStations', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', display: 'none' })
      ),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
  imports: [
    ...MODULES,
    ...COMPONENTS,
    ...PIPES,
    ...DIRECTIVES,
    MatCard,
    MatTab,
    MatTabContent,
    MatTabGroup,
    MatTabLabel,
    DatexPipe,
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    MatDivider,
    MatListItemIcon,
    MatListItemLine,
    MatListOption,
    MatSelectionList,
    MatCheckbox,
  ],
})
export class ErpComponent implements OnInit {
  private platform = inject(Platform);
  private breakpointObserver = inject(BreakpointObserver);
  private auth = inject(AuthService);
  private erpService = inject(ErpService);
  public eppService = inject(EppService);
  private smwService = inject(SmwService);

  stationNames = {};
  stations = signal([]);
  regions = signal([]);

  parameterSets = signal([]);
  selectedParameterSet = signal<any>(undefined);
  parametersetSelectControl = new UntypedFormControl();
  defaultParametersetDetails = signal<Record<string, any[]>>({});
  userParametersets = signal<Record<string, any[]>>({});
  selectedUserParameters = signal<Record<string, string[]>>({});

  settingsParametersByResolutionAndSet = signal<
    Record<number, Record<string, any[]>>
  >({
    1: {},
    12: {},
  });
  parameterNames = {};

  neutralColor = signal('green');
  erpData = signal({});

  erpSettings = {
    previousParameterSet: null,
    parameterSets: null,
  };

  showSettings = signal(false);

  tabsSelectedIndex = signal(0);
  tabsValues = [1, 12];

  hours = signal([]);
  windpowerMode = signal(false);
  solarpowerMode = signal(false);

  days = [];

  hoveredColumnIndex: number | null = null;
  selectedColumnIndex: number | null = null;

  private handset = toSignal(
    this.breakpointObserver
      .observe(['(max-width: 900px)'])
      .pipe(map(r => r.matches)),
    { initialValue: this.breakpointObserver.isMatched(['(max-width: 900px)']) }
  );

  readonly isMobilePlatform = computed(
    () => this.handset() || this.platform.ANDROID || this.platform.IOS
  );

  ngOnInit() {
    this.initComponent();
  }

  async initComponent() {
    this.initDays();
    await this.eppService.getColorMappingNames();
    await this.eppService.getColorMappings();
    this.readErpSettings();
    this.initErp();
  }

  initDays() {
    const today = moment();
    for (let i = 0; i < 8; i++) {
      const day = moment(today).add(i, 'days');
      this.days.push(day);
    }
  }

  initErp(): void {
    const customerDocument = this.auth.getCustomerDocument();
    const eppProduct = customerDocument['eppProducts'][0];
    this.initStations(eppProduct);
    this.initRegions(eppProduct);
    this.initParameterSets();
    this.initHours();
    this.initParameters().then(() => {
      if (
        (this.selectedUserParameters()[this.selectedParameterSet()] ?? [])
          .length > 0
      ) {
        this.getErpData();
      }
    });
  }

  async getErpData(): Promise<void> {
    const hours = [];
    for (const region of this.regions()) {
      const data = await this.erpService.getErpData(
        this.selectedParameterSet(),
        region.id,
        this.tabsValues[this.tabsSelectedIndex()]
      );
      this.erpData.update(erpData => {
        erpData[region.id] = data;
        return { ...erpData };
      });
      if (hours.length === 0) {
        for (const dataset of data['aggregated_forecasts']) {
          const dtg = moment(dataset['valid_at']);
          const hour = dtg.format('HH');
          hours.push(hour);
        }
        this.hours.set([...hours]);
      }
    }
  }

  initHours(): void {
    this.hours.set([]);
    const now = moment();
    for (let hour = 0; hour < 24; hour++) {
      const dtg = moment(now).add(hour + 1, 'hours');
      this.hours.update(hours => {
        hours.push(dtg.format('HH'));
        return [...hours];
      });
    }
  }

  initParameterSets(): void {
    this.parameterSets.set([...this.eppService.getParameterSets()]);
    if (this.parameterSets().length > 0) {
      if (this.erpSettings.previousParameterSet) {
        if (
          this.parameterSets().indexOf(this.erpSettings.previousParameterSet) >=
          0
        ) {
          this.selectedParameterSet.set(this.erpSettings.previousParameterSet);
          this.parametersetSelectControl.setValue(this.selectedParameterSet());
          this.setEnergyModes();
          return;
        }
      }
      this.selectedParameterSet.set(this.parameterSets()[0]);
      this.setEnergyModes();

      this.parametersetSelectControl.setValue(this.selectedParameterSet());
      this.erpSettings.previousParameterSet = this.selectedParameterSet();
      this.saveErpSettings();
    }
  }

  initRegions(eppProduct): void {
    const regions = [];
    for (const region of eppProduct['regions']) {
      if (region.stations.length > 0) {
        region['expanded'] = false;
        const stations = region.stations;
        stations.sort((a, b) =>
          stationName(a, this.stations())
            .toLowerCase()
            .localeCompare(stationName(b, this.stations()).toLowerCase())
        );
        regions.push({ ...region, stations });
      }
    }
    this.regions.set([...regions]);
  }

  initStations(eppProduct): void {
    this.stations.set([
      ...eppProduct.stations.sort((a, b) =>
        a.overwriteStationName > b.overwriteStationName ? 1 : -1
      ),
    ]);
  }

  setSelectedUserParameters(): void {
    const selectedUserParameters: Record<string, string[]> = {};

    for (const parameterset of this.parameterSets()) {
      const userParameterset = this.userParametersets()[parameterset] ?? [];

      selectedUserParameters[parameterset] = userParameterset
        .filter(
          p =>
            p.selected && p.hourly == this.tabsValues[this.tabsSelectedIndex()]
        )
        .map(p => p.parameter);
    }

    this.selectedUserParameters.set(selectedUserParameters);
  }

  async initParameters(): Promise<void> {
    return new Promise<void>(resolve => {
      this.eppService
        .getDefaultParametersetDetails(this.parameterSets())
        .then(details => {
          this.defaultParametersetDetails.set({ ...details });
          this.extractParameterNames();
          this.userParametersets.set({});

          if (this.erpSettings.parameterSets) {
            for (const parameterset of this.parameterSets()) {
              if (!isNil(this.erpSettings.parameterSets[parameterset])) {
                const newParameters = this.checkForNewParameters(
                  parameterset,
                  this.erpSettings.parameterSets[parameterset]
                );
                this.userParametersets.update(current => {
                  return { ...current, [parameterset]: [...newParameters] };
                });
              } else {
                const parameters = this.toUserParameterset(
                  this.defaultParametersetDetails()[parameterset] ?? []
                );
                this.userParametersets.update(current => {
                  return { ...current, [parameterset]: [...parameters] };
                });
              }
            }
          } else {
            for (const parameterset of this.parameterSets()) {
              const parameters = this.toUserParameterset(
                this.defaultParametersetDetails()[parameterset] ?? []
              );
              this.userParametersets.update(current => {
                return { ...current, [parameterset]: [...parameters] };
              });
            }
          }

          this.setSelectedUserParameters();
          resolve();
        });
    });
  }

  checkForNewParameters(parameterset: string, parameters: Array<any>): any {
    const checkedParameters = [...parameters];

    for (const detail of this.defaultParametersetDetails()[parameterset] ??
      []) {
      const exists = parameters.some(
        p => p.parameter === detail.fe_parameter && p.hourly === detail.hourly
      );

      if (!exists && detail.aggregation_method) {
        checkedParameters.push({
          parameter: detail.fe_parameter,
          selected: detail.is_region_standard,
          hourly: detail.hourly,
        });
      }
    }

    return checkedParameters;
  }

  toUserParameterset(parametersetDetails: any): any {
    const userParameterset = [];
    const pushedParameterKeys = [];

    for (const detail of parametersetDetails) {
      const parameterKey = `${detail.fe_parameter}_${detail.hourly}`;

      if (!pushedParameterKeys.includes(parameterKey)) {
        if (detail.aggregation_method) {
          userParameterset.push({
            parameter: detail.fe_parameter,
            selected: detail.is_region_standard,
            hourly: detail.hourly,
          });
          pushedParameterKeys.push(parameterKey);
        }
      }
    }

    return userParameterset;
  }

  extractParameterNames(): void {
    for (const set of this.parameterSets()) {
      const names = [];
      for (const details of this.defaultParametersetDetails()[set] ?? []) {
        if (names.indexOf(details.fe_parameter) < 0) {
          names.push(details.fe_parameter);
        }
      }
      this.parameterNames[set] = names;
    }
  }

  saveErpSettings(): void {
    localStorage.setItem('erpSettings', JSON.stringify(this.erpSettings));
    const userProfile = this.auth.getUserProfile();
    if (userProfile) {
      let global = userProfile['metadata']['global'];
      if (!global) {
        global = {};
      }
      global['erp'] = this.erpSettings;
      global['eopTableColorScheme'] = this.neutralColor();
      this.auth.updateUserData('global', global);
    }
  }

  readErpSettings(): void {
    const storedSettings = localStorage.getItem('erpSettings');
    let settings;
    if (storedSettings) {
      settings = JSON.parse(storedSettings);
    } else {
      const userProfile = this.auth.getUserProfile();
      const global = userProfile['metadata']['global'];
      if (global && global.hasOwnProperty('erp')) {
        settings = global['erp'];
      }
    }
    if (settings) {
      for (const key of Object.keys(this.erpSettings)) {
        if (settings.hasOwnProperty(key)) {
          this.erpSettings[key] = settings[key];
        }
      }
    }
  }

  setEnergyModes() {
    this.windpowerMode.set(this.selectedParameterSet() === 'energy-wind-power');
    this.solarpowerMode.set(
      this.selectedParameterSet() === 'energy-solar-power'
    );
  }

  onParameterSetChange(event: MatOptionSelectionChange): void {
    if (event.isUserInput) {
      this.selectedParameterSet.set(event.source.value);
      this.erpSettings.previousParameterSet = this.selectedParameterSet();
      this.saveErpSettings();
      this.setEnergyModes();
      this.erpData.set({});
      if (
        (this.selectedUserParameters()[this.selectedParameterSet()] ?? [])
          .length > 0
      ) {
        this.getErpData();
      }
    }
  }

  onSetupSettings(): void {
    const nextState: Record<number, Record<string, any[]>> = {
      1: {},
      12: {},
    };

    for (const resolution of this.tabsValues) {
      for (const parameterset of this.parameterSets()) {
        nextState[resolution][parameterset] = (
          this.userParametersets()[parameterset] ?? []
        )
          .filter(entry => entry.hourly === resolution)
          .map(entry => ({ ...entry }));
      }
    }

    this.settingsParametersByResolutionAndSet.set(nextState);
    this.showSettings.set(true);
  }

  onDrop(
    event: CdkDragDrop<any[]>,
    resolution: number,
    parameterset: string
  ): void {
    moveItemInArray(
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );

    this.settingsParametersByResolutionAndSet.update(settings => {
      return {
        ...settings,
        [resolution]: {
          ...settings[resolution],
          [parameterset]: [...event.container.data],
        },
      };
    });
  }

  onParameterSelectionChange(
    event: MatSelectionListChange,
    resolution: number,
    parameterset: string
  ): void {
    const changedOption = event.options[0];
    const selectedParameter = changedOption.value;

    this.settingsParametersByResolutionAndSet.update(settings => {
      const entries = [...(settings?.[resolution]?.[parameterset] ?? [])];
      const index = entries.findIndex(
        entry => entry.parameter === selectedParameter
      );

      if (index >= 0) {
        entries[index] = {
          ...entries[index],
          selected: changedOption.selected,
        };
      }

      return {
        ...settings,
        [resolution]: {
          ...settings[resolution],
          [parameterset]: entries,
        },
      };
    });
  }

  getSettingsParameters(resolution: number, parameterset: string): any[] {
    return (
      this.settingsParametersByResolutionAndSet()?.[resolution]?.[
        parameterset
      ] ?? []
    );
  }

  getSelectedParametersCountForResolutionAndSet(
    resolution: number,
    parameterset: string
  ): number {
    return this.getSettingsParameters(resolution, parameterset).filter(
      entry => entry.selected
    ).length;
  }

  getTotalParametersCountForResolutionAndSet(
    resolution: number,
    parameterset: string
  ): number {
    return this.getSettingsParameters(resolution, parameterset).length;
  }

  hasSettingsParameters(resolution: number, parameterset: string): boolean {
    return this.getSettingsParameters(resolution, parameterset).length > 0;
  }

  onCancelSettings(): void {
    this.showSettings.set(false);
  }

  onSaveSettings(): void {
    const settings = this.settingsParametersByResolutionAndSet();
    const nextUserParametersets: Record<string, any[]> = {};

    for (const parameterset of this.parameterSets()) {
      nextUserParametersets[parameterset] = [
        ...(settings?.[1]?.[parameterset] ?? []).map(entry => ({ ...entry })),
        ...(settings?.[12]?.[parameterset] ?? []).map(entry => ({ ...entry })),
      ];
    }

    this.userParametersets.set(nextUserParametersets);

    this.erpSettings.parameterSets = this.userParametersets();
    this.saveErpSettings();
    this.showSettings.set(false);
    this.setSelectedUserParameters();
    this.erpData.set({});

    if (
      (this.selectedUserParameters()[this.selectedParameterSet()] ?? [])
        .length > 0
    ) {
      this.getErpData();
    }
  }

  onRestoreStandardSettings(): void {
    const nextState: Record<number, Record<string, any[]>> = {
      1: {},
      12: {},
    };

    for (const resolution of this.tabsValues) {
      for (const parameterset of this.parameterSets()) {
        nextState[resolution][parameterset] = this.toUserParameterset(
          this.defaultParametersetDetails()[parameterset]
        )
          .filter(entry => entry.hourly === resolution)
          .map(entry => ({ ...entry }));
      }
    }

    this.settingsParametersByResolutionAndSet.set(nextState);
  }

  isAllSelected(resolution: number, parameterset: string): boolean {
    const params = this.getSettingsParameters(resolution, parameterset);
    return params.length > 0 && params.every(p => p.selected);
  }

  isSomeSelected(resolution: number, parameterset: string): boolean {
    const params = this.getSettingsParameters(resolution, parameterset);
    const selectedCount = params.filter(p => p.selected).length;
    return selectedCount > 0 && selectedCount < params.length;
  }

  toggleSelectAll(checked: boolean, resolution: number, parameterset: string) {
    const params = this.getSettingsParameters(resolution, parameterset);
    params.forEach(p => {
      p.selected = checked;
    });
  }

  onResetSettings(): void {
    for (const parameterset of this.parameterSets()) {
      this.userParametersets.update(userParametersets => {
        return {
          ...userParametersets,
          [parameterset]: this.toUserParameterset(
            this.defaultParametersetDetails()[parameterset]
          ),
        };
      });
    }

    this.erpSettings.parameterSets = this.userParametersets();

    if (!this.eppService.getGreenTableDesign()) {
      this.onChangeNeutralColor('green');
    }

    this.saveErpSettings();
    this.showSettings.set(false);
    this.setSelectedUserParameters();
    this.erpData.set({});

    if (
      (this.selectedUserParameters()[this.selectedParameterSet()] ?? [])
        .length > 0
    ) {
      this.getErpData();
    }
  }

  onChangeRegionExpand(i: number): void {
    this.regions.update(regions => {
      regions[i].expanded = !regions[i].expanded;
      return [...regions];
    });
  }

  colorMapping(selectedParameterSet, parameter, element) {
    return this.eppService.colorMapping(
      selectedParameterSet,
      parameter,
      element
    );
  }

  onToggleColor(isGreen: boolean) {
    const newColor = isGreen ? 'green' : 'white';
    this.onChangeNeutralColor(newColor);
  }

  public onChangeNeutralColor(color: string): void {
    if (color !== this.neutralColor()) {
      this.neutralColor.set(color);
      this.eppService.setGreenTableDesign(this.neutralColor() === 'green');
      this.smwService.greenTableDesign.set(this.neutralColor() === 'green');
      localStorage.setItem('eopTableColorScheme', color);
      const userProfile = this.auth.getUserProfile();
      if (userProfile) {
        const global = userProfile['metadata']['global'];
        if (global) {
          global['eopTableColorScheme'] = this.neutralColor();
          this.auth.updateUserData('global', global);
        }
      }
    }
  }

  getCurrentTabResolution(): number {
    return this.tabsValues[this.tabsSelectedIndex()];
  }

  // On mobile, the region/station name is rendered as its own full-width row
  // instead of a leftmost column, so it needs an explicit colspan covering
  // the icon column plus all forecast columns of the currently active tab.
  readonly mobileRegionRowColspan = computed(() => {
    const dataColumns =
      this.getCurrentTabResolution() === 1
        ? this.hours().length
        : this.days.length * 2;
    return 1 + dataColumns;
  });

  onTabChanged(event: MatTabChangeEvent): void {
    this.tabsSelectedIndex.set(event.index);

    if (this.showSettings()) {
      this.onSetupSettings();
    }

    this.setSelectedUserParameters();
    this.erpData.set({});
    this.getErpData();
  }
}
